import type { LngLatCoord, RoutePathOption, RoutePlan } from '@gpx-spa/geo-domain'
import {
  computeCorridorBuffers,
  type LineBufferGeometryCache,
  type CorridorBufferPathInput,
} from '@gpx-spa/geo-adapters'
import { pathIdForSegment } from '@/utils/device-api-shipment-payload'
import { rawCoordinatesForFormPath } from './path-vertex-stride'

export function buildCorridorInputsForSelectedPaths(
  segmentIndex: number,
  paths: RoutePathOption[],
  selectedByPathId: Record<string, boolean>,
  qualityPercent: number,
  radiusMeters: number
): CorridorBufferPathInput[] {
  const corridorInputs: CorridorBufferPathInput[] = []
  for (let i = paths.length - 1; i >= 0; i--) {
    const p = paths[i]
    const pid = pathIdForSegment(segmentIndex, p!, i)
    if (!selectedByPathId[pid]) continue
    const raw = rawCoordinatesForFormPath(p)
    if (!raw || raw.length < 2) continue
    corridorInputs.push({
      pathId: pid,
      raw,
      qualityPercent,
      radiusMeters,
    })
  }
  return corridorInputs
}

/**
 * Runs worker (or sync) corridor computation for paths that are not yet in the cache, then primes the cache.
 */
export async function ensureCorridorBuffersForSelectedPaths(
  geoCache: LineBufferGeometryCache,
  corridorInputs: CorridorBufferPathInput[],
  radiusMeters: number,
  qualityPercent: number
): Promise<{ msTurf: number }> {
  const uncached = corridorInputs.filter(
    (it) => !geoCache.hasCorridorBufferCached(it.pathId, it.radiusMeters, it.qualityPercent)
  )
  if (uncached.length === 0) {
    return { msTurf: 0 }
  }
  const t0 = performance.now()
  const corridorBufferResults = await computeCorridorBuffers(uncached)
  const msTurf = performance.now() - t0
  for (const r of corridorBufferResults) {
    geoCache.primeCorridorResult(r.pathId, radiusMeters, qualityPercent, r.simplified, r.buffer)
  }
  return { msTurf }
}

export type PayloadGeometryMaps = {
  pathCoordinatesById: Record<string, LngLatCoord[]>
  corridorsByPathId: Record<string, ReturnType<LineBufferGeometryCache['getBufferedPolygon']>>
}

/** Same coordinate / corridor resolution as map overlays — used for Device API JSON preview. */
export function collectPayloadGeometryMaps(
  segmentIndex: number,
  paths: RoutePathOption[],
  selectedByPathId: Record<string, boolean>,
  qualityPercent: number,
  corridorBufferM: number,
  geoCache: LineBufferGeometryCache
): PayloadGeometryMaps {
  const pathCoordinatesById: Record<string, LngLatCoord[]> = {}
  const corridorsByPathId: PayloadGeometryMaps['corridorsByPathId'] = {}
  for (let i = 0; i < paths.length; i++) {
    const p = paths[i]
    const pid = pathIdForSegment(segmentIndex, p!, i)
    const raw = rawCoordinatesForFormPath(p)
    if (!selectedByPathId[pid] || !raw || raw.length < 2) continue
    const coords = geoCache.getSimplified(pid, raw, qualityPercent)
    pathCoordinatesById[pid] = coords
    corridorsByPathId[pid] = geoCache.getBufferedPolygon(pid, coords, corridorBufferM, qualityPercent)
  }
  return { pathCoordinatesById, corridorsByPathId }
}

/** Merge geometry maps for every segment in the plan (multi-leg itineraries). */
export function collectPayloadGeometryMapsForRoutePlan(
  routePlan: RoutePlan,
  selectedByPathId: Record<string, boolean>,
  qualityPercent: number,
  corridorBufferM: number,
  geoCache: LineBufferGeometryCache
): PayloadGeometryMaps {
  const pathCoordinatesById: Record<string, LngLatCoord[]> = {}
  const corridorsByPathId: PayloadGeometryMaps['corridorsByPathId'] = {}
  for (let s = 0; s < routePlan.segments.length; s++) {
    const paths = routePlan.segments[s]?.paths ?? []
    const part = collectPayloadGeometryMaps(
      s,
      paths,
      selectedByPathId,
      qualityPercent,
      corridorBufferM,
      geoCache
    )
    Object.assign(pathCoordinatesById, part.pathCoordinatesById)
    Object.assign(corridorsByPathId, part.corridorsByPathId)
  }
  return { pathCoordinatesById, corridorsByPathId }
}

export async function ensureCorridorBuffersForRoutePlan(
  routePlan: RoutePlan,
  selectedByPathId: Record<string, boolean>,
  geoCache: LineBufferGeometryCache,
  corridorBufferM: number,
  geometryQualityPercent: number
): Promise<void> {
  for (let s = 0; s < routePlan.segments.length; s++) {
    const paths = routePlan.segments[s]?.paths ?? []
    const inputs = buildCorridorInputsForSelectedPaths(
      s,
      paths,
      selectedByPathId,
      geometryQualityPercent,
      corridorBufferM
    )
    await ensureCorridorBuffersForSelectedPaths(geoCache, inputs, corridorBufferM, geometryQualityPercent)
  }
}
